import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
    Box, Paper, Typography, Button, TextField, MenuItem,
    Alert, CircularProgress, FormControlLabel, Checkbox, Snackbar,
    useTheme, useMediaQuery, Tabs, Tab, IconButton,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import MedicalServicesOutlinedIcon from '@mui/icons-material/MedicalServicesOutlined';
import PsychologyOutlinedIcon from '@mui/icons-material/PsychologyOutlined';
import LocalBarOutlinedIcon from '@mui/icons-material/LocalBarOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import api from '../services/api';
import { confirmAcao } from '../services/alerts';
import BuscaPaciente from './BuscaPaciente';
import { isVisualizador } from '../services/auth';

const ORIGEM_OPCOES = [
    { v: 'ESPONTANEA', l: 'Espontânea' },
    { v: 'UBS', l: 'UBS' },
    { v: 'CAPS', l: 'CAPS' },
    { v: 'ESCOLA', l: 'Escola' },
    { v: 'JUDICIARIO', l: 'Judiciário' },
    { v: 'MEDICO', l: 'Médico' },
];

const ALCOOL_OPCOES = [
    { v: 'NAO_USA', l: 'Não usa' },
    { v: 'OCASIONAL', l: 'Ocasional' },
    { v: 'USO_RISCO', l: 'Uso de risco' },
    { v: 'ABUSO', l: 'Abuso' },
    { v: 'DEPENDENCIA', l: 'Dependência' },
];

const estadoInicial = () => ({
    idPaciente: null,
    queixaEDemanda: { queixaPrincipal: '', historiaDemandaAtual: '', origemEncaminhamento: 'ESPONTANEA' },
    historicoClinico: {
        diagnosticosPrevios: '', historicoDoencas: '', historicoTraumasAcidentes: false,
        descricaoTraumas: '', medicamentosEmUso: '', alergias: '',
    },
    historicoPsiquiatrico: {
        tratamentoPsicologicoAnterior: false, detalheTratamentoAnterior: '',
        internacaoPsiquiatricaPrevia: false, historicoPsiquiatricoFamiliar: '',
    },
    usoDeSubstancias: {
        usoTabaco: false, usoOutrasSubstancias: false, descricaoSubstancias: '', usoAlcool: 'NAO_USA',
    },
    contextoPsicossocial: {
        situacaoFamiliar: '', situacaoOcupacional: '', suporteRedeSocial: '', impactoNaRotina: '',
    },
    planoInicial: { hipoteseInicialDemanda: '', objetivosIniciais: '', observacoesGerais: '' },
});

const SectionTitle = ({ children, descricao }) => (
    <Box sx={{ mb: descricao ? 2 : 3 }}>
        <Typography variant="h6" sx={{ color: '#1ABB9C', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 4, height: 24, bgcolor: '#1ABB9C', borderRadius: 1 }} />
            {children}
        </Typography>
        {descricao && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, ml: 1.5 }}>
                {descricao}
            </Typography>
        )}
    </Box>
);

export default function AnamneseForm() {
    const { id } = useParams();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const isEdit = Boolean(id);
    const isView = searchParams.get('view') === '1';

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const soLeitura = isView || isVisualizador();

    const [tab, setTab] = useState(0);
    const [form, setForm] = useState(estadoInicial());
    const [pacienteSelecionado, setPacienteSelecionado] = useState(null);
    const [fetching, setFetching] = useState(isEdit);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    useEffect(() => {
        if (!isEdit) return;
        (async () => {
            try {
                const { data } = await api.get(`/anamneses/${id}`);
                setForm({
                    idPaciente: data.idPaciente,
                    queixaEDemanda: data.queixaEDemanda || estadoInicial().queixaEDemanda,
                    historicoClinico: data.historicoClinico || estadoInicial().historicoClinico,
                    historicoPsiquiatrico: data.historicoPsiquiatrico || estadoInicial().historicoPsiquiatrico,
                    usoDeSubstancias: data.usoDeSubstancias || estadoInicial().usoDeSubstancias,
                    contextoPsicossocial: data.contextoPsicossocial || estadoInicial().contextoPsicossocial,
                    planoInicial: data.planoInicial || estadoInicial().planoInicial,
                });
                setPacienteSelecionado({ id_paciente: data.idPaciente, nomeCompleto: data.nomePaciente });
            } catch {
                setError('Não foi possível carregar a anamnese.');
            } finally {
                setFetching(false);
            }
        })();
    }, [id, isEdit]);

    const setSecao = (secao, campo, valor) => {
        setForm(prev => ({ ...prev, [secao]: { ...prev[secao], [campo]: valor } }));
    };

    const solicitarSalvar = async () => {
        setError('');
        if (!isEdit && !form.idPaciente) {
            setError('Selecione um paciente.');
            return;
        }
        if (!form.queixaEDemanda.queixaPrincipal?.trim()) {
            setError('Queixa principal é obrigatória.');
            setTab(0);
            return;
        }

        const ok = await confirmAcao({
            title: 'Confirmar alterações',
            text: 'Deseja gravar os dados desta anamnese?',
            confirmButtonText: 'Confirmar e salvar',
            cancelButtonText: 'Rever dados',
            icon: 'question',
        });
        if (ok) executarSalvar();
    };

    const executarSalvar = async () => {
        setSaving(true);
        try {
            if (isEdit) {
                await api.put(`/anamneses/${id}`, form);
                setSnackbar({ open: true, message: 'Anamnese atualizada com sucesso!', severity: 'success' });
            } else {
                await api.post('/anamneses', form);
                setSnackbar({ open: true, message: 'Anamnese cadastrada com sucesso!', severity: 'success' });
            }
            setTimeout(() => navigate('/anamnese'), 1500);
        } catch (err) {
            const msg = err?.response?.data;
            setError(typeof msg === 'string' ? msg : 'Erro ao salvar os dados. Verifique a ligação com o servidor.');
        } finally {
            setSaving(false);
        }
    };

    if (fetching) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
                <CircularProgress sx={{ color: '#1ABB9C' }} />
            </Box>
        );
    }

    // --- Sections ---

    const SecaoQueixa = (
        <Box>
            <SectionTitle descricao="Motivo principal e contexto da procura por atendimento.">Queixa e Demanda</SectionTitle>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <TextField fullWidth label="Queixa Principal" required disabled={soLeitura}
                    value={form.queixaEDemanda.queixaPrincipal}
                    onChange={(e) => setSecao('queixaEDemanda', 'queixaPrincipal', e.target.value)} />
                <TextField select fullWidth label="Origem do Encaminhamento" disabled={soLeitura}
                    value={form.queixaEDemanda.origemEncaminhamento || ''}
                    onChange={(e) => setSecao('queixaEDemanda', 'origemEncaminhamento', e.target.value)}>
                    {ORIGEM_OPCOES.map(o => <MenuItem key={o.v} value={o.v}>{o.l}</MenuItem>)}
                </TextField>
                <TextField fullWidth multiline minRows={4} label="História da Demanda Atual" disabled={soLeitura}
                    value={form.queixaEDemanda.historiaDemandaAtual}
                    onChange={(e) => setSecao('queixaEDemanda', 'historiaDemandaAtual', e.target.value)} />
            </Box>
        </Box>
    );

    const SecaoHistClinico = (
        <Box>
            <SectionTitle descricao="Diagnósticos, doenças, traumas, medicamentos e alergias do paciente.">Histórico Clínico</SectionTitle>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                    <TextField fullWidth label="Diagnósticos Prévios" disabled={soLeitura}
                        value={form.historicoClinico.diagnosticosPrevios}
                        onChange={(e) => setSecao('historicoClinico', 'diagnosticosPrevios', e.target.value)} />
                    <TextField fullWidth label="Histórico de Doenças" disabled={soLeitura}
                        value={form.historicoClinico.historicoDoencas}
                        onChange={(e) => setSecao('historicoClinico', 'historicoDoencas', e.target.value)} />
                </Box>
                <FormControlLabel
                    control={<Checkbox disabled={soLeitura}
                        checked={form.historicoClinico.historicoTraumasAcidentes}
                        onChange={(e) => setSecao('historicoClinico', 'historicoTraumasAcidentes', e.target.checked)} />}
                    label="Histórico de traumas/acidentes"
                />
                {form.historicoClinico.historicoTraumasAcidentes && (
                    <TextField fullWidth multiline minRows={2} label="Descrição dos Traumas" disabled={soLeitura}
                        value={form.historicoClinico.descricaoTraumas}
                        onChange={(e) => setSecao('historicoClinico', 'descricaoTraumas', e.target.value)} />
                )}
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                    <TextField fullWidth label="Medicamentos em Uso" disabled={soLeitura}
                        value={form.historicoClinico.medicamentosEmUso}
                        onChange={(e) => setSecao('historicoClinico', 'medicamentosEmUso', e.target.value)} />
                    <TextField fullWidth label="Alergias" disabled={soLeitura}
                        value={form.historicoClinico.alergias}
                        onChange={(e) => setSecao('historicoClinico', 'alergias', e.target.value)} />
                </Box>
            </Box>
        </Box>
    );

    const SecaoHistPsi = (
        <Box>
            <SectionTitle descricao="Tratamentos psicológicos anteriores, internações e histórico familiar.">Histórico Psiquiátrico</SectionTitle>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                    <FormControlLabel
                        control={<Checkbox disabled={soLeitura}
                            checked={form.historicoPsiquiatrico.tratamentoPsicologicoAnterior}
                            onChange={(e) => setSecao('historicoPsiquiatrico', 'tratamentoPsicologicoAnterior', e.target.checked)} />}
                        label="Tratamento psicológico anterior"
                    />
                    <FormControlLabel
                        control={<Checkbox disabled={soLeitura}
                            checked={form.historicoPsiquiatrico.internacaoPsiquiatricaPrevia}
                            onChange={(e) => setSecao('historicoPsiquiatrico', 'internacaoPsiquiatricaPrevia', e.target.checked)} />}
                        label="Internação psiquiátrica prévia"
                    />
                </Box>
                {form.historicoPsiquiatrico.tratamentoPsicologicoAnterior && (
                    <TextField fullWidth multiline minRows={2} label="Detalhes do Tratamento Anterior" disabled={soLeitura}
                        value={form.historicoPsiquiatrico.detalheTratamentoAnterior}
                        onChange={(e) => setSecao('historicoPsiquiatrico', 'detalheTratamentoAnterior', e.target.value)} />
                )}
                <TextField fullWidth multiline minRows={2} label="Histórico Psiquiátrico Familiar" disabled={soLeitura}
                    value={form.historicoPsiquiatrico.historicoPsiquiatricoFamiliar}
                    onChange={(e) => setSecao('historicoPsiquiatrico', 'historicoPsiquiatricoFamiliar', e.target.value)} />
            </Box>
        </Box>
    );

    const SecaoSubst = (
        <Box>
            <SectionTitle descricao="Padrão de uso de tabaco, álcool e outras substâncias.">Uso de Substâncias</SectionTitle>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                    <FormControlLabel
                        control={<Checkbox disabled={soLeitura}
                            checked={form.usoDeSubstancias.usoTabaco}
                            onChange={(e) => setSecao('usoDeSubstancias', 'usoTabaco', e.target.checked)} />}
                        label="Uso de tabaco"
                    />
                    <FormControlLabel
                        control={<Checkbox disabled={soLeitura}
                            checked={form.usoDeSubstancias.usoOutrasSubstancias}
                            onChange={(e) => setSecao('usoDeSubstancias', 'usoOutrasSubstancias', e.target.checked)} />}
                        label="Uso de outras substâncias"
                    />
                </Box>
                <TextField select fullWidth label="Uso de Álcool" disabled={soLeitura}
                    value={form.usoDeSubstancias.usoAlcool || ''}
                    onChange={(e) => setSecao('usoDeSubstancias', 'usoAlcool', e.target.value)}>
                    {ALCOOL_OPCOES.map(o => <MenuItem key={o.v} value={o.v}>{o.l}</MenuItem>)}
                </TextField>
                {form.usoDeSubstancias.usoOutrasSubstancias && (
                    <TextField fullWidth multiline minRows={2} label="Descrição das Substâncias" disabled={soLeitura}
                        value={form.usoDeSubstancias.descricaoSubstancias}
                        onChange={(e) => setSecao('usoDeSubstancias', 'descricaoSubstancias', e.target.value)} />
                )}
            </Box>
        </Box>
    );

    const SecaoContexto = (
        <Box>
            <SectionTitle descricao="Situação familiar, ocupacional, rede de apoio e impacto na rotina.">Contexto Psicossocial</SectionTitle>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <TextField fullWidth multiline minRows={3} label="Situação Familiar" disabled={soLeitura}
                    value={form.contextoPsicossocial.situacaoFamiliar}
                    onChange={(e) => setSecao('contextoPsicossocial', 'situacaoFamiliar', e.target.value)} />
                <TextField fullWidth multiline minRows={3} label="Situação Ocupacional" disabled={soLeitura}
                    value={form.contextoPsicossocial.situacaoOcupacional}
                    onChange={(e) => setSecao('contextoPsicossocial', 'situacaoOcupacional', e.target.value)} />
                <TextField fullWidth multiline minRows={3} label="Suporte / Rede Social" disabled={soLeitura}
                    value={form.contextoPsicossocial.suporteRedeSocial}
                    onChange={(e) => setSecao('contextoPsicossocial', 'suporteRedeSocial', e.target.value)} />
                <TextField fullWidth multiline minRows={3} label="Impacto na Rotina" disabled={soLeitura}
                    value={form.contextoPsicossocial.impactoNaRotina}
                    onChange={(e) => setSecao('contextoPsicossocial', 'impactoNaRotina', e.target.value)} />
            </Box>
        </Box>
    );

    const SecaoPlano = (
        <Box>
            <SectionTitle descricao="Hipótese diagnóstica inicial, objetivos terapêuticos e observações gerais.">Plano Inicial</SectionTitle>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <TextField fullWidth multiline minRows={2} label="Hipótese Inicial / Demanda" disabled={soLeitura}
                    value={form.planoInicial.hipoteseInicialDemanda}
                    onChange={(e) => setSecao('planoInicial', 'hipoteseInicialDemanda', e.target.value)} />
                <TextField fullWidth multiline minRows={2} label="Objetivos Iniciais" disabled={soLeitura}
                    value={form.planoInicial.objetivosIniciais}
                    onChange={(e) => setSecao('planoInicial', 'objetivosIniciais', e.target.value)} />
                <TextField fullWidth multiline minRows={3} label="Observações Gerais" disabled={soLeitura}
                    value={form.planoInicial.observacoesGerais}
                    onChange={(e) => setSecao('planoInicial', 'observacoesGerais', e.target.value)} />
            </Box>
        </Box>
    );

    return (
        <Box sx={{ flexGrow: 1, pb: soLeitura ? 4 : 10, px: isMobile ? 1 : 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
                <IconButton onClick={() => navigate('/anamnese')} sx={{ bgcolor: '#fff', boxShadow: '0 2px 5px rgba(0,0,0,0.05)' }}>
                    <ArrowBackIcon />
                </IconButton>
                <Typography variant={isMobile ? 'h5' : 'h4'} sx={{ fontWeight: 'bold', color: '#333' }}>
                    {isView ? 'Visualizar Anamnese' : isEdit ? 'Editar Anamnese' : 'Nova Anamnese'}
                </Typography>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

            <Paper elevation={0} sx={{ borderRadius: 2, boxShadow: '0 2px 10px rgba(0,0,0,0.05)', overflow: 'hidden' }}>
                <Box sx={{ p: { xs: 2, sm: 3 }, borderBottom: '1px solid #eee', bgcolor: '#fafafa' }}>
                    <BuscaPaciente
                        label="Paciente *"
                        disabled={isEdit || soLeitura}
                        value={pacienteSelecionado}
                        onChange={(v) => { setPacienteSelecionado(v); setForm(f => ({ ...f, idPaciente: v?.id_paciente || null })); }}
                    />
                </Box>

                <Tabs
                    value={tab}
                    onChange={(_, v) => setTab(v)}
                    variant={isMobile ? 'scrollable' : 'standard'}
                    scrollButtons="auto"
                    sx={{
                        borderBottom: '1px solid #eee',
                        bgcolor: '#fafafa',
                        '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, minHeight: 56 },
                        '& .Mui-selected': { color: '#1ABB9C !important' },
                        '& .MuiTabs-indicator': { backgroundColor: '#1ABB9C', height: 3 },
                    }}
                >
                    <Tab icon={<ChatBubbleOutlineIcon />} iconPosition="start" label="Queixa" />
                    <Tab icon={<MedicalServicesOutlinedIcon />} iconPosition="start" label="Hist. Clínico" />
                    <Tab icon={<PsychologyOutlinedIcon />} iconPosition="start" label="Hist. Psiquiátrico" />
                    <Tab icon={<LocalBarOutlinedIcon />} iconPosition="start" label="Substâncias" />
                    <Tab icon={<GroupsOutlinedIcon />} iconPosition="start" label="Contexto" />
                    <Tab icon={<AssignmentOutlinedIcon />} iconPosition="start" label="Plano Inicial" />
                </Tabs>

                <Box sx={{ p: { xs: 2, sm: 4 } }}>
                    {tab === 0 && SecaoQueixa}
                    {tab === 1 && SecaoHistClinico}
                    {tab === 2 && SecaoHistPsi}
                    {tab === 3 && SecaoSubst}
                    {tab === 4 && SecaoContexto}
                    {tab === 5 && SecaoPlano}
                </Box>
            </Paper>

            {!soLeitura && (
                <Paper
                    elevation={3}
                    sx={{
                        position: 'fixed',
                        bottom: 0,
                        left: { xs: 0, md: 240 },
                        right: 0,
                        px: 3, py: 1.5,
                        display: 'flex',
                        justifyContent: 'flex-end',
                        gap: 1,
                        bgcolor: '#fff',
                        borderTop: '1px solid #eee',
                        zIndex: 10,
                    }}
                >
                    <Button variant="outlined" onClick={() => navigate('/anamnese')} sx={{ color: '#666', borderColor: '#ccc' }}>
                        Cancelar
                    </Button>
                    <Button variant="contained" onClick={solicitarSalvar} disabled={saving}
                        startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                        sx={{ bgcolor: '#1ABB9C', '&:hover': { bgcolor: '#129A80' }, px: 4 }}>
                        {isEdit ? 'Atualizar Anamnese' : 'Salvar Anamnese'}
                    </Button>
                </Paper>
            )}

            <Snackbar open={snackbar.open} autoHideDuration={4000}
                onClose={() => setSnackbar(s => ({ ...s, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert onClose={() => setSnackbar(s => ({ ...s, open: false }))} severity={snackbar.severity} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}
